import { useCallback, useEffect, useState } from 'react';
import { FaFileUpload, FaChild } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { getCargasFamiliares, cargarExcelCargasFamiliares } from '../../services/cargasFamiliaresService';
import '../../styles/pages/RRHH/CargasFamiliaresPage.css';

/**
 * Cargas familiares vigentes de la empresa.
 *
 * Se cargan con el Excel "Trabajadores con Cargas Autorizadas por IPS" que se
 * descarga de Previred. Cada carga reemplaza lo anterior. Las liquidaciones leen de
 * acá el tramo y las cargas: sin haber cargado el Excel al menos una vez, no se
 * pueden crear liquidaciones.
 */
const CargasFamiliaresPage = ({ id_company }) => {
    const [datos, setDatos] = useState(null);
    const [archivo, setArchivo] = useState(null);
    const [cargando, setCargando] = useState(false);

    const buscar = useCallback(async () => {
        try {
            setDatos(await getCargasFamiliares(id_company));
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'No se pudieron obtener las cargas familiares', 'error');
        }
    }, [id_company]);

    useEffect(() => { buscar(); }, [buscar]);

    const handleCargar = async (e) => {
        e.preventDefault();
        if (!archivo) return;

        setCargando(true);
        try {
            const respuesta = await cargarExcelCargasFamiliares(id_company, archivo);
            const sinExcel = respuesta.data.colaboradores_sin_excel || [];
            Swal.fire({
                icon: 'success',
                title: 'Cargas familiares actualizadas',
                html: `<p>${respuesta.message}</p>` + (sinExcel.length
                    ? `<p>Colaboradores activos que no vienen en el Excel (quedan sin asignación): ${sinExcel.map(c => c.nombre).join(', ')}</p>`
                    : '')
            });
            setArchivo(null);
            document.getElementById('excelCargasInput').value = null;
            buscar();
        } catch (error) {
            Swal.fire('No se cargó el Excel', error.response?.data?.message || 'Error al cargar el Excel', 'error');
        } finally {
            setCargando(false);
        }
    };

    const periodo = datos?.cargas?.[0]?.periodo_excel;
    const fechaCarga = datos?.cargas?.[0]?.fecha_carga;

    return (
        <div className="cargas-container">
            <div className="cargas-header">
                <h1><FaChild /> Cargas Familiares</h1>
                <p>Cargas y tramo de asignación familiar autorizados por el IPS, según el Excel de Previred</p>
            </div>

            <div className="cargas-seccion">
                <h2><FaFileUpload /> Cargar Excel de Previred</h2>
                <p className="cargas-ayuda">
                    Suba el Excel «Trabajadores con Cargas Autorizadas por IPS» (hojas ConCarga y SinCarga). Cada carga
                    reemplaza la anterior. Cárguelo cada julio, cuando se renuevan los tramos, y cada vez que cambien las
                    cargas de alguien. Sin haberlo cargado al menos una vez no se pueden crear liquidaciones.
                </p>
                <form onSubmit={handleCargar} className="cargas-form">
                    <input
                        id="excelCargasInput"
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={(e) => setArchivo(e.target.files[0] || null)}
                        disabled={cargando}
                    />
                    <button type="submit" className="btn-cargar" disabled={cargando || !archivo}>
                        {cargando ? 'Cargando...' : 'Cargar Excel'}
                    </button>
                </form>
            </div>

            <div className="cargas-seccion">
                <h2>Cargas vigentes</h2>
                {!datos ? null : !datos.excel_cargado ? (
                    <p className="cargas-aviso">Esta empresa aún no carga el Excel de cargas familiares de Previred.</p>
                ) : (
                    <>
                        <p className="cargas-ayuda">
                            Excel del período {periodo ? `${periodo.slice(4)}/${periodo.slice(0, 4)}` : '—'}
                            {fechaCarga && `, cargado el ${new Date(fechaCarga).toLocaleDateString('es-CL')}`}.
                        </p>
                        <table className="cargas-tabla">
                            <thead>
                                <tr>
                                    <th>RUT</th>
                                    <th>Colaborador</th>
                                    <th>Simples</th>
                                    <th>Maternales</th>
                                    <th>Inválidas</th>
                                    <th>Tramo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {datos.cargas.map(c => (
                                    <tr key={c.id_colaborador}>
                                        <td>{c.rut}</td>
                                        <td>{`${c.nombres} ${c.apellido_paterno} ${c.apellido_materno || ''}`.trim()}</td>
                                        <td>{c.cargas_simples}</td>
                                        <td>{c.cargas_maternales}</td>
                                        <td>{c.cargas_invalidas}</td>
                                        <td>{c.tramo}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
                {datos?.colaboradores_sin_excel?.length > 0 && (
                    <p className="cargas-aviso">
                        Colaboradores activos que no vienen en el Excel (sin asignación familiar):{' '}
                        {datos.colaboradores_sin_excel.map(c => `${c.nombre} (${c.rut})`).join(', ')}
                    </p>
                )}
            </div>
        </div>
    );
};

export default CargasFamiliaresPage;
